// MailAccountSettingsCard — the "Mail" card of the account settings page
// (architecture/MAIL.md §3.4), mounted beside Language / Accessibility /
// Security in `MemberProfile`.
//
// It reads the SAME setup gate the /mail empty state and the drawer read, so a
// person who has no mailbox is taught the setup here too (MailSetupPanel is
// mounted below the list) instead of meeting an empty card. With accounts
// present the card is the account list: address, kind, status, and the two
// per-account fields §3.4 names first — signature and default send-as.
//
// Every write goes through `mail.accounts` update / remove. `remove` is the
// DISCONNECT: the server tombstones the row and purges the local mirror
// behind it, so it asks first through the house confirm — the question says
// what goes for this row (functions/accounts.js `disconnectMailAccount`).
//
// Design: the house card (CommonCard) with SettingRow's row anatomy. No
// dividers between rows — each row's own padding is the separation
// (design-direction.md §8), and the hover wash SettingRow already declares is
// the boundary cue.

// One connected mailbox. Fed `childrenAs: 'state'`, so its state is a
// `mail.accounts list` row: { id, address, kind, provider, status, sendAs,
// signature, settings, … }.
//
// ⚠️ NAMED `MailSettingsAccountRow`, NOT `MailAccountRow` — Rule 63, and it
// was a live P0, not a precaution. `components/Sidebar.js:62` already exports
// a `MailAccountRow` (the sidebar's mailbox row: icon + label + attention
// dot), and BOTH files ride the eager slice, so the two `export *`s collided
// in one barrel. The sidebar's definition won: measured on a direct /profile
// load with two accounts injected, this card's AccountList rendered children
// keyed `Icon` / `Label` / `AttentionDot` — the sidebar row — so the address,
// kind · status sub-line, send-as picker, signature field and Disconnect this
// component defines had never reached the settings page at all. The rename is
// the fix (Rule 63's "rename your new component — do NOT shadow"); the
// sidebar keeps its name and its two references untouched.
export const MailSettingsAccountRow = {
  flow: 'y',
  gap: 'Z',
  padding: 'A 0',

  Head: {
    extends: 'SettingRow',
    padding: '0',
    ':hover': { background: 'transparent' },

    Info: {
      Name: { text: (el, s) => s.address || '' },
      Sub: {
        // SettingRow's own Sub hides itself when neither `el.description` nor
        // `s.description` is set. This row composes its sub-line from up to
        // three TRANSLATED words instead (kind · status · simulated), which
        // must resolve at render time so a language switch reaches them — so
        // the gate is restated as "always shown" and the text is built here.
        hide: () => false,
        text: (el, s) => {
          const kindKeys = {
            personal: 'mail.settings.kind.personal',
            tenant: 'mail.settings.kind.tenant',
            shared: 'mail.settings.kind.shared'
          }
          const statusKeys = {
            active: 'mail.settings.status.active',
            syncing: 'mail.settings.status.syncing',
            reauth_required: 'mail.settings.status.reauth',
            disabled: 'mail.settings.status.disabled'
          }
          const kind = kindKeys[s.kind] ? el.call('polyglot', kindKeys[s.kind]) : s.kind || ''
          const status = statusKeys[s.status]
            ? el.call('polyglot', statusKeys[s.status])
            : s.status || ''
          // A SIMULATED mailbox is seeded demo data — no provider is ever
          // contacted for it. `serializeAccount` states the fact
          // (`simulated: boolean`), so it joins the sub-line as a third word
          // instead of replacing either of the two already there: the row is
          // genuinely a Company mailbox and genuinely Live, and it is also
          // not real. All three are true at once.
          const simulated = s.simulated ? el.call('polyglot', 'mail.settings.simulated') : ''
          return [kind, status, simulated].filter(Boolean).join(' · ')
        }
      }
    },

    Right: {
      // The admin kit's compact tonal button (it was an undefined `ghost`
      // theme).
      ReconnectBtn: {
        if: (el, s) => s.status === 'reauth_required',
        extends: 'AdminActionBtnCompact',
        text: '{{ mail.settings.action.reconnect | polyglot }}',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('reconnectMailAccount', s.id)
        }
      },
      // [Disconnect] — one control for every row. The house confirm it opens
      // names what goes (functions/accounts.js `disconnectMailAccount`): a
      // real mailbox keeps its mail at the provider, a simulated one is
      // deleted for good. The button itself writes nothing.
      DisconnectBtn: {
        // The admin kit's compact tonal button (it was an undefined `ghost`
        // theme).
        extends: 'AdminActionBtnCompact',
        // The semantic danger INK pair, never a raw hue: `red` is one literal
        // that is wrong on one of the two schemes (DESIGN_LANGUAGE §10
        // raw-hue-ink, pinned by shared/__tests__/designLanguageLocks).
        color: 'dangerInk',
        // FA515 arming — a factory subscribes only to what it actually READ,
        // so a run that short-circuits on `s.root` never tracks the key behind
        // it. The declared selector is read up front inside the same effect.
        disabledDeps: [(el, s) => s.root && s.root.mailSettingsBusy],
        disabled: (el, s) => {
          const busy = s.root && s.root.mailSettingsBusy
          return !!(busy && busy.id === s.id)
        },
        text: '{{ mail.settings.action.disconnect | polyglot }}',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('disconnectMailAccount', s.id)
        }
      }
    }
  },

  // ── The two fields §3.4 names first ──
  Fields: {
    flow: 'y',
    gap: 'Z',

    SendAsField: {
      // A native select: `sendAs` is the server's own allowlist and
      // `defaultSendAs` must be one of it, so the control cannot offer a value
      // the PATCH would refuse. Hidden when the mailbox has one address only —
      // a one-option picker is chrome with nothing to choose.
      if: (el, s) => ((s.sendAs || []).length || 0) > 1,
      flow: 'y',
      gap: 'V',

      Label: {
        tag: 'label',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption',
        text: '{{ mail.settings.field.sendAs | polyglot }}'
      },
      Select: {
        tag: 'select',
        width: '100%',
        theme: 'field',
        minHeight: 'controlH',
        round: 'radiusControl',
        color: 'title',
        fontSize: 'Z2',
        padding: '0 A',
        // `childrenAs: 'props'` — a native <option> built with
        // `childrenAs: 'state'` carries NO `value` attribute, so
        // `e.target.value` would answer the LABEL instead of the address.
        childrenAs: 'props',
        children: (el, s) =>
          (s.sendAs || []).map((row) => ({
            key: row.address,
            value: row.address,
            text: row.name ? `${row.name} <${row.address}>` : row.address
          })),
        childProps: {
          tag: 'option',
          selectedDeps: [(el, s) => s.root && s.root.mailSettingsDraft],
          selected: (el, s) => {
            const draft = ((s.root && s.root.mailSettingsDraft) || {})[s.id] || {}
            const current =
              draft.defaultSendAs ||
              ((s.sendAs || []).find((r) => r && r.default) || {}).address ||
              s.address
            return String(current || '') === String(el.value || '') ? 'selected' : null
          }
        },
        onChange: (e, el, s) => {
          el.call('setMailAccountDraft', s.id, { defaultSendAs: e.target.value })
        }
      }
    },

    SignatureField: {
      flow: 'y',
      gap: 'V',

      Label: {
        tag: 'label',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption',
        text: '{{ mail.settings.field.signature | polyglot }}'
      },
      Input: {
        tag: 'textarea',
        rows: '3',
        width: '100%',
        theme: 'field',
        round: 'radiusControl',
        color: 'title',
        fontSize: 'Z2',
        fontWeight: '400',
        lineHeight: '1.5',
        padding: 'Z A',
        resize: 'vertical',
        valueDeps: [(el, s) => s.root && s.root.mailSettingsDraft],
        value: (el, s) => {
          const draft = ((s.root && s.root.mailSettingsDraft) || {})[s.id] || {}
          if (typeof draft.signature === 'string') return draft.signature
          return (s.signature && s.signature.text) || ''
        },
        onInput: (e, el, s) => {
          el.call('setMailAccountDraft', s.id, { signature: e.target.value })
        }
      }
    },

    RowFooter: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z1',
      // The box is RESERVED, never `if:`-gated: a save button that appears on
      // the first keystroke shoves the next account row down the page
      // (Rule 66). Only visibility and reachability change.
      minHeight: 'controlHCompact',

      RowError: {
        tag: 'p',
        margin: '0',
        minWidth: '0',
        fontSize: 'Y2',
        fontWeight: '400',
        lineHeight: '1.4',
        color: 'dangerInk',
        textDeps: [(el, s) => s.root && s.root.mailSettingsError],
        text: (el, s) => {
          const err = s.root && s.root.mailSettingsError
          return err && err.id === s.id ? err.message || '' : ''
        }
      },

      SaveBtn: {
        extends: 'Button',
        theme: 'primary',
        minHeight: 'controlHCompact',
        round: 'radiusPill',
        padding: '0 A',
        fontSize: 'Y2',
        fontWeight: '700',
        flexShrink: '0',
        text: '{{ mail.settings.action.save | polyglot }}',
        visibilityDeps: [(el, s) => s.root && s.root.mailSettingsDraft],
        visibility: (el, s) => {
          const draft = ((s.root && s.root.mailSettingsDraft) || {})[s.id]
          return draft && Object.keys(draft).length ? 'visible' : 'hidden'
        },
        pointerEventsDeps: [(el, s) => s.root && s.root.mailSettingsDraft],
        pointerEvents: (el, s) => {
          const draft = ((s.root && s.root.mailSettingsDraft) || {})[s.id]
          return draft && Object.keys(draft).length ? 'auto' : 'none'
        },
        tabindexDeps: [(el, s) => s.root && s.root.mailSettingsDraft],
        tabindex: (el, s) => {
          const draft = ((s.root && s.root.mailSettingsDraft) || {})[s.id]
          return draft && Object.keys(draft).length ? '0' : '-1'
        },
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('saveMailAccountSettings', s.id)
        }
      }
    }
  }
}

export const MailAccountSettingsCard = {
  // The account page's card shape, mirrored rather than imported: it lives in
  // a module-private `_card()` inside the shared package's MemberProfile.js,
  // which this package must not reach into. Any drift there is a drift here,
  // and __tests__/mailSettingsCardShape.test.js holds the two together: the
  // solid `card` surface, `radiusCard`, inset B (A on a phone or a narrow
  // slot), no measure cap — the settings stack is the cap — and the row gap
  // B of the main-stack cards it sits among.
  flow: 'y',
  padding: 'B',
  round: 'radiusCard',
  theme: 'card',
  minWidth: '0',
  gap: 'B',
  '@mobileL': { padding: 'A' },
  '@appMobile': { padding: 'A' },
  // The scroll-nav of the account page matches this attribute against its own
  // entry; the value is an opaque KEY and never goes through polyglot (see the
  // page's `sections` factory).
  attr: { 'data-content-section': 'Mail' },

  subscribeTo: [
    'mailAccounts',
    'mailSetup',
    'mailSettingsDraft',
    'mailSettingsBusy',
    'mailSettingsError'
  ],

  // The full account rows (sendAs, signature) come from `mail.accounts list`;
  // the gate's own summaries carry address + status only.
  onCreate: (el) => {
    el.call('loadMailAccounts')
  },

  CardTitle: {
    tag: 'h2',
    margin: '0',
    fontSize: 'B',
    fontWeight: '700',
    color: 'title',
    text: '{{ mail.settings.title | polyglot }}'
  },
  CardHint: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    fontWeight: '400',
    lineHeight: '1.45',
    color: 'caption',
    text: '{{ mail.settings.description | polyglot }}'
  },

  // A failure of the LIST itself (`mail.accounts list` refused — a 5xx, an
  // expired session) carries `id: null`, because it belongs to no row. The
  // row renderer matches `err.id === s.id` and therefore showed nothing: the
  // card painted its title, its one-line hint and an empty space, and said
  // not one word about why it was empty. This is where an id-less failure is
  // read out loud.
  CardError: {
    ifDeps: [(el, s) => s.root && s.root.mailSettingsError],
    if: (el, s) => {
      const err = s.root && s.root.mailSettingsError
      return !!(err && err.id == null)
    },
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    fontWeight: '500',
    lineHeight: '1.45',
    color: 'dangerInk',
    textDeps: [(el, s) => s.root && s.root.mailSettingsError],
    text: (el, s) => {
      const err = s.root && s.root.mailSettingsError
      return (err && err.message) || el.call('polyglot', 'mail.settings.listFailed')
    }
  },

  AccountList: {
    hide: (el, s) => !((s.root && s.root.mailAccounts) || []).length,
    hideDeps: [(el, s) => s.root && s.root.mailAccounts],
    flow: 'y',
    // The distinctive name, never the collided one — see the component's own
    // header. A bare `MailAccountRow` here resolves to the SIDEBAR's row.
    childExtends: 'MailSettingsAccountRow',
    childrenAs: 'state',
    childrenDeps: [(el, s) => s.root && s.root.mailAccounts],
    children: (el, s) =>
      ((s.root && s.root.mailAccounts) || []).map((a) => ({ key: a && a.id, ...a }))
  },

  // No mailbox yet → the same gate the /mail page and the drawer render, so
  // "connect another account" and "connect your first one" are one panel
  // (§3.4 "[Connect another account] → the same panel as the empty state").
  //
  // A mount-site `if:` REPLACES the base component's own gate, so the base's
  // `off` rule is restated here beside the card's own `ready` rule: with a
  // working mailbox this card is the account LIST, not a setup teacher.
  MailSetupPanel: {
    ifDeps: [(el, s) => s.root && s.root.mailSetup],
    if: (el, s) => {
      const setup = s.root && s.root.mailSetup
      if (!setup) return true
      return setup.state !== 'off' && setup.state !== 'ready'
    },
    // The admin's TENANT instructions do not belong on a member's personal
    // settings page — they are six long cards about a domain-wide delegation,
    // and this card's job is "which mailboxes are mine". The panel's own
    // [Set up mail] button is the way there (it opens the admin surface), so
    // the steps stay on /mail where the app's own empty state teaches them.
    // Measured live 2026-09-05: with the steps mounted this card ran 1697px
    // tall inside a 744px track of otherwise short preference cards.
    SetupBody: { SetupSteps: { if: () => false } }
  }
}
